import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { FilterStore, UserStore } from '../../stores';
import { inject, observer } from 'mobx-react';
import { computed } from 'mobx';
import { withRouter } from 'react-router';
import { Ref } from '../../utilities/Ref';

interface NavigationItem {
    isSeparator?: boolean;
    isHeader?: boolean;
    show?: boolean;
    title?: string;
    route?: string;
    icon?: string;
    allowedRoles: string[];
    allowedPermissions?: string[];
    requiredPermissions?: string[];
    forbiddenPermissions?: string[];
    items: NavigationItem[];
    keyLookUp?: string;
    hideCheck?: boolean;
}

const superAdmins = ['superAdmin'];
const siteAdmins = superAdmins.concat(['siteAdmin']);
const associates = siteAdmins.concat(['ponderaAssociate']);
const organizationAdmins = associates.concat(['organizationAdmin']);
const managers = organizationAdmins.concat(['manager']);
const users = managers.concat(['user']);
const superSiteOrg = ['superAdmin', 'siteAdmin', 'organizationAdmin'];

const navItems: NavigationItem[] = [
    {
        route: '/pvastyles',
        title: 'The 8 PVA Styles',
        icon: 'fas fa-address-card',
        allowedRoles: users,
        items: []
    },
    {
        isSeparator: true,
        allowedRoles: users,
        items: []
    },
    {
        isHeader: true,
        show: true,
        title: 'My Reports',
        allowedRoles: users,
        items: [
            {
                route: '/myreport',
                keyLookUp: 'myreport',
                title: 'My PVA Individual Report',
                icon: 'glyphicon glyphicon-tasks',
                allowedRoles: users,
                items: []
            },
            {
                route: '/myinteractionreport',
                keyLookUp: 'myinteractionreport',
                title: 'My PVA Interaction Report',
                icon: 'glyphicon glyphicon-list-alt',
                allowedRoles: users,
                forbiddenPermissions: ['limitedReport'],
                items: []
            }
        ]
    },
    {
        isSeparator: true,
        allowedRoles: users,
        items: []
    },
    {
        isHeader: true,
        show: true,
        title: 'Team Reports',
        allowedRoles: managers,
        items: [
            {
                route: '/individualreport',
                keyLookUp: 'individualReport',
                title: 'PVA Individual Report',
                icon: 'fas fa-portrait',
                allowedRoles: managers,
                items: []
            },
            {
                route: '/comparisonreport',
                keyLookUp: 'comparisonReport',
                title: 'PVA Comparison',
                icon: 'glyphicon glyphicon-transfer',
                allowedRoles: managers,
                items: []
            },
            {
                route: '/interactionreport',
                keyLookUp: 'interactionReport',
                title: 'PVA Interaction Report',
                icon: 'fas fa-user-friends',
                allowedRoles: managers,
                items: []
            },
            {
                route: '/teamologyreport',
                keyLookUp: 'teamologyReport',
                title: 'PVA Teamology',
                icon: 'fas fa-grip-horizontal',
                allowedRoles: managers,
                allowedPermissions: ['teamology'],
                items: []
            },
            {
                isSeparator: true,
                allowedRoles: organizationAdmins,
                items: []
            }
        ]
    },
    {
        isHeader: true,
        show: true,
        title: 'Old Team Reports',
        allowedRoles: managers,
        items: [
            {
                route: '/scorecard',
                keyLookUp: 'scoreCard',
                title: 'PVA Score Card',
                icon: 'fas fa-award',
                allowedRoles: siteAdmins,
                allowedPermissions: ['viewFusionScore'],
                items: [],
                hideCheck: true
            },
            {
                isSeparator: true,
                allowedRoles: siteAdmins,
                items: []
            }
        ]
    },
    {
        isHeader: true,
        show: false,
        title: 'Administrative',
        allowedRoles: siteAdmins,
        items: [
            {
                isHeader: true,
                show: false,
                title: 'Configuration Settings',
                allowedRoles: users,
                items: [
                    {
                        route: '/tenant',
                        title: 'Tenants',
                        icon: 'fas fa-users',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/organization',
                        title: 'Organizations',
                        icon: 'fas fa-building',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/team',
                        title: 'Teams',
                        icon: 'fas fa-sitemap',
                        allowedRoles: organizationAdmins,
                        allowedPermissions: ['viewTeams', 'manageAllTeams', 'createTeams'],
                        items: []
                    },
                    {
                        route: '/language',
                        title: 'Languages',
                        icon: 'fas fa-language',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/user',
                        title: 'Users',
                        icon: 'glyphicon glyphicon-user',
                        allowedRoles: organizationAdmins,
                        allowedPermissions: ['editUsers'],
                        items: []
                    },
                    {
                        route: '/userscore',
                        title: 'User Scores',
                        icon: 'fas fa-chart-line',
                        allowedRoles: superAdmins,
                        allowedPermissions: ['editScores'],
                        requiredPermissions: ['editScores'],
                        items: []
                    },
                    {
                        route: '/scoreComparison',
                        title: 'Score Comparison',
                        icon: 'fas fa-chart-area',
                        allowedRoles: superAdmins,
                        allowedPermissions: ['editScores'],
                        requiredPermissions: ['editScores'],
                        items: []
                    },
                    {
                        route: '/survey',
                        title: 'Surveys',
                        icon: 'fas fa-book',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/surveyinvite',
                        title: 'Survey Invites',
                        icon: 'fas fa-envelope-open',
                        allowedRoles: superSiteOrg,
                        allowedPermissions: ['SendAssessments'],
                        items: []
                    },
                    {
                        route: '/sitesettings',
                        title: 'Site Settings',
                        icon: 'fas fa-anchor',
                        allowedRoles: superAdmins,
                        items: []
                    }
                ]
            },
            {
                isHeader: true,
                show: false,
                title: 'Report Settings',
                allowedRoles: siteAdmins,
                items: [
                    {
                        route: '/responses',
                        title: 'Responses',
                        icon: 'fas fa-pencil-alt',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/9boxsettings',
                        title: '9 Box Settings',
                        icon: 'glyphicon glyphicon-th',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/teamologysettings',
                        title: 'Teamology Settings',
                        icon: 'glyphicon glyphicon-th',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        route: '/scorecards',
                        title: 'Score Card Settings',
                        icon: 'fas fa-award',
                        allowedRoles: siteAdmins,
                        items: []
                    },
                    {
                        isSeparator: true,
                        allowedRoles: siteAdmins,
                        items: []
                    }
                ]
            },
        ]
    },
    {
        isHeader: true,
        show: false,
        title: 'Other Reports',
        allowedRoles: siteAdmins,
        items: [
            {
                route: '/notes',
                title: 'Exchange Notes',
                icon: 'fas fa-sticky-note',
                allowedRoles: siteAdmins,
                allowedPermissions: ['ExchangeNotes'],
                items: []
            },
            {
                route: '/teamreport',
                keyLookUp: 'teamReport',
                title: 'PVA Team Report',
                icon: 'fas fa-sliders-h',
                allowedRoles: siteAdmins,
                allowedPermissions: ['teamReport'],
                items: []
            },
            {
                route: '/teamgrid',
                keyLookUp: 'teamGridReport',
                title: 'PVA Team Grid',
                icon: 'fas fa-th-large',
                allowedRoles: siteAdmins,
                allowedPermissions: ['grid'],
                items: []
            },
            {
                route: '/teamlistviewreport',
                keyLookUp: 'teamListViewReport',
                title: 'PVA Team List View',
                icon: 'glyphicon glyphicon-sort-by-attributes',
                allowedRoles: siteAdmins,
                allowedPermissions: ['listView'],
                items: []
            },
            {
                route: '/teamlistviewrireport',
                keyLookUp: 'teamListViewReportRI',
                title: 'PVA Team List View RI',
                icon: 'glyphicon glyphicon-sort-by-attributes',
                allowedRoles: siteAdmins,
                allowedPermissions: ['listViewRI'],
                items: []
            },
            {
                route: '/9box',
                keyLookUp: '9Box',
                title: 'PVA 9-Box',
                icon: 'glyphicon glyphicon-th',
                allowedRoles: siteAdmins,
                allowedPermissions: ['view9Box'],
                items: []
            },
            {
                route: '/rfactor',
                keyLookUp: 'rfactor',
                title: 'R Factor',
                icon: 'fas fa-award',
                allowedRoles: [],
                allowedPermissions: ['rFactorReport'],
                items: [],
                hideCheck: true
            },
        ]
    },
];

interface NavProps {
    FilterStore?: FilterStore;
    UserStore?: UserStore;
}

@inject('FilterStore', 'UserStore') @observer
class NavItems extends React.Component<NavProps> {
    private indexCounter: number = 0;

    constructor(props: NavProps) {
        super(props);
    }

    @computed get organization() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization;
        }
        return null;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    @computed get keyValues() {
        if (this.props.FilterStore && this.props.FilterStore.keyValues) {
            return this.props.FilterStore.keyValues;
        }
        return [];
    }

    @computed get userPermissions() {
        if (this.props.UserStore && this.props.UserStore.userLoaded && this.props.UserStore.userInfo.userPermissions) {
            return this.props.UserStore!.userInfo.userPermissions;
        }
        return [];
    }

    @computed get userRole() {
        if (this.props.UserStore && this.props.UserStore.userLoaded) {
            return this.props.UserStore!.userInfo.userRole;
        }
        return null;
    }

    private filterForbiddenPermissions(navEntry: NavigationItem) {
        if (!!navEntry.forbiddenPermissions) {
            const allowed = navEntry.forbiddenPermissions.every((forbidden: string) => !this.props.UserStore!.checkUserPermission(forbidden));
            return allowed;
        }

        return true;
    }

    private filterRequiredPermissions(navEntry: NavigationItem) {
        if (!navEntry.requiredPermissions || navEntry.requiredPermissions.length === 0) {
            // if there is no required permission array, we need to base whether they can see the menu
            // item off of the allowed roles and allowed permissions.
            const hasAllowedRole = navEntry.allowedRoles.some((allowedRole: string) => allowedRole === this.userRole);
            const hasAllowedPermission = navEntry.allowedPermissions == null ? false : navEntry.allowedPermissions.some((allowedPermission: string) => this.props.UserStore!.checkUserPermission(allowedPermission));
            
            return hasAllowedRole || hasAllowedPermission;
        }

        if (navEntry.requiredPermissions.length > 0) {
            var allFound = true;
            navEntry.requiredPermissions.map((perm: string) => {
                if (this.userPermissions.indexOf(perm) === -1) {
                    allFound = false;
                }
            });
            if (allFound) {
                return true;
            }
        }

        if (this.userRole === 'siteAdmin' || this.userRole === 'superAdmin') {
            return true;
        }

        return false;
    }

    private GetKeyOverride(key?: string, fallback?: string) {
        if (key && this.props.FilterStore) {
            return this.props.FilterStore.GetKeyOverride(key, fallback || '');
        }
        return fallback;
    }

    private filterItems(subItem: NavigationItem) {
        if (subItem.hideCheck) {
            if (subItem.keyLookUp === 'scoreCard') {
                if (!this.organization) {
                    return false;
                }
                if (this.organization.scoreCardCount === 0) {
                    return false;
                }
            }
        }
        if (subItem.isHeader && this.countItemsUnderHeader(subItem) > 0) {
            return true;
        }
        return (this.filterForbiddenPermissions(subItem) && ((this.filterRequiredPermissions(subItem)) ||
            subItem.allowedRoles.some(allowedRole => allowedRole === this.userRole)));
    }

    private willShow(navEntry: NavigationItem) {
        return !navEntry.isHeader || navEntry.items.filter(this.filterItems.bind(this)).length > 0;
    }

    private countItemsUnderHeader = (navEntry: any): number => {
        if (navEntry.isHeader) {
            let count: number = 0;
            navEntry.items.forEach((i: NavigationItem) => {
                count += this.countItemsUnderHeader(i);
            });
            return count;
        }
        if (!navEntry.isSeparator && this.filterItems(navEntry)) {
            return 1;
        }
        return 0;
    }

    private handleSubItem = (navEntry: any) => {
        if (navEntry.isHeader) {
            if (this.countItemsUnderHeader(navEntry) < 1) {
                return;
            }
            return (
                <div className="nav-item-container" key={`navitem-${this.indexCounter++}`}>
                    {this.organization && <div id={`organization-${this.organization.id}`} />}
                    {this.keyValues && <div id={`keyValues-${this.keyValues.length}`} />}

                    <div className="side-nav-header" onClick={(e) => { navEntry.show = !navEntry.show; this.forceUpdate(); }}>
                        <div className="side-nav-header-text">
                            <h3>{this.GetKeyOverride(navEntry.keyLookUp, navEntry.title)}</h3>
                        </div>
                        <div className="side-nav-header-toggle">
                            {
                                navEntry.show && <span className="glyphicon glyphicon-triangle-bottom" />
                            }
                            {
                                !navEntry.show && <span className="glyphicon glyphicon-triangle-right" />
                            }
                        </div>
                    </div>
                    {
                        navEntry.show &&
                        navEntry.items
                            .filter(this.filterItems.bind(this))
                            .map(this.handleSubItem)
                    }
                </div>
            );
        }

        if (navEntry.isSeparator) {
            return <div key={`navitem-${this.indexCounter++}`} className="separator"><div className="separator-inner" /></div>;
        }

        if (!this.filterItems(navEntry)) {
            return;
        }

        return (
            <li key={`navitem-${this.indexCounter++}`}>
                <NavLink to={navEntry.route!} activeClassName="active">
                    <span className={navEntry.icon} /> {this.GetKeyOverride(navEntry.keyLookUp, navEntry.title)}
                </NavLink>
            </li>
        );
    }

    public render() {
        let jsxNavItems = navItems
            .filter(this.willShow.bind(this))
            .map(this.handleSubItem);

        // insert a home nav item for everyone
        jsxNavItems.unshift(
            <li key={`navitem-home`}>
                <NavLink to="/home" activeClassName="active">
                    <span className="glyphicon glyphicon-home" /> Home
                </NavLink>
            </li>
        );

        return jsxNavItems;
    }
}

export default withRouter(NavItems);


// WEBPACK FOOTER //
// ./src/components/navigation/NavItems.tsx